import React, { useEffect, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";
import { COURSE_BASE_URL } from "../../services/courseService";
import Loading from "../../utils/Loading";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { useAuthContext } from "../../hooks/useAuthContext";
import { getRoleDisplay } from "../../utils/getRoleDisplay";

const CourseTable = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const fetchCourseData = async () => {
    try {
      const response = await fetch(`${COURSE_BASE_URL}/api/courses/list`);
      if (!response.ok) {
        throw new Error("Unable to fetch courses data!");
      }
      const data = await response.json();
      setCourses(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setError(error);
      setLoading(false);
    }
  };

  const deleteCourse = async (e, _id) => {
    e.preventDefault();

    try {
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (confirmResult.isConfirmed) {
        const response = await fetch(
          `${COURSE_BASE_URL}/api/courses/list/delete/${_id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        const json = await response.json();
        if (!response.ok) {
          console.log(error.message);
          throw new Error(json.error || "Failed to delete course");
        }
        Swal.fire({
          title: "Deleted!",
          text: "course has been deleted.",
          icon: "success",
          timer: 2000,
        });

        fetchCourseData(); // reload courses list right away
      }
    } catch (error) {
      console.error("Error deleting customer: ", error.message);
      throw new Error("Failed to delete course!");
    }
  };

  useEffect(() => {
    fetchCourseData();
  }, []);

  return (
    <section className="course-table">
      <div className="container">
        <div className="row">
          {!loading && !error && courses.length > 0 ? (
            <h1 className="bg-white p-2">
              Course List <hr />
            </h1>
          ) : (
            ""
          )}
        </div>

        {/* Course table list */}
        <div className="row">
          <div className="col">
            {loading && <Loading />}
            {error && <div className="alert alert-danger">{error.message}</div>}

            {!loading && !error && courses.length === 0 && (
              <div className="alert alert-danger mt-4" role="alert">
                <p className="text-center lead">
                  New courses are coming soon. Please check back again!
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Course Table */}
        {courses.length > 0 && (
          <div className="row">
            <div className="col-lg-12">
              <div className="table-responsive">
                <table className="table" style={{ fontSize: "14px" }}>
                  <thead className="table-dark">
                    <tr>
                      <th scope="col">Course Title</th>
                      <th scope="col">Instructor</th>
                      <th scope="col">Price</th>
                      <th scope="col">Credit</th>
                      <th scope="col">Category</th>
                      <th scope="col"># Of Courses</th>

                      {courses.courseRating ? (
                        <th scope="col">Course Rating</th>
                      ) : (
                        <th scope="col"></th>
                      )}
                      <th scope="col">Created At</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((course) => (
                      <tr key={course._id}>
                        <td>
                          <i
                            className="fa fa-book bg-dark text-white p-1 rounded-circle"
                            aria-hidden="true"
                          ></i>{" "}
                          &nbsp;
                          <Link
                            to={`/api/dashboard/course-content-details/${course._id}`}
                          >
                            {course.title}
                          </Link>
                        </td>
                        <td>{course.instructor}</td>
                        <td className="text-danger">
                          {parseFloat(course.price).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) === 0
                            ? "FREE"
                            : `${parseFloat(course.price).toLocaleString(
                                "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}`}
                        </td>

                        <td>
                          {course.credit === 0 ? (
                            <span className="text-success">FREE</span>
                          ) : (
                            `${course.credit} `
                          )}
                        </td>

                        <td className="text-muted categoryxx">
                          <i>{course.category}</i>
                        </td>
                        <td>
                          {course.numberOfCourses > 1 ? (
                            <span>{course.numberOfCourses} courses</span>
                          ) : (
                            <span>{course.numberOfCourses} course</span>
                          )}{" "}
                          available
                        </td>
                        <td className="text-warning">
                          {course.courseRating && course.courseRating}
                        </td>

                        <td className="text-muted created_at">
                          {format(new Date(course.createdAt), "MMMM d, yyyy")}
                        </td>
                        <td>
                          {user && user.isAdmin && getRoleDisplay() && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              title="Delete course"
                              onClick={(e) => deleteCourse(e, course._id)}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CourseTable;
